import { useMemo, useState } from "react";
import { Tags, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProductBehavior, ProductTypeDefinitionDto } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { showQueryLoader } from "../lib/query-utils";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import { Badge, Button, Input, SearchSelect, Textarea, Toggle, useConfirm } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

const BEHAVIOR_OPTIONS: { value: ProductBehavior; label: string; hint: string }[] = [
  { value: "SINGLE", label: "Physical item", hint: "Inventory, shipping, and stock tracking" },
  { value: "BUNDLE", label: "Bundle", hint: "Built from bill-of-materials components" },
  { value: "PACK", label: "Pack", hint: "Mystery pack listing — configure pool under Packs" },
  { value: "CREDIT_PACK", label: "Credit pack", hint: "Grants store credits — no shipping" },
];

interface TypeForm {
  key: string;
  label: string;
  description: string;
  behavior: ProductBehavior;
  sortOrder: string;
  active: boolean;
}

function emptyForm(): TypeForm {
  return { key: "", label: "", description: "", behavior: "SINGLE", sortOrder: "0", active: true };
}

function rowToForm(row: ProductTypeDefinitionDto): TypeForm {
  return {
    key: row.key,
    label: row.label,
    description: row.description ?? "",
    behavior: row.behavior,
    sortOrder: String(row.sortOrder),
    active: row.active,
  };
}

function behaviorLabel(behavior: ProductBehavior): string {
  return BEHAVIOR_OPTIONS.find((o) => o.value === behavior)?.label ?? behavior;
}

export function ProductTypes() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ProductTypeDefinitionDto | null>(null);
  const [form, setForm] = useState<TypeForm>(emptyForm());

  const canWrite = can("catalog.write");

  const listQuery = useQuery({
    queryKey: ["catalog-product-types"],
    queryFn: () => api<{ types: ProductTypeDefinitionDto[] }>("/catalog/product-types?includeInactive=true"),
  });

  const types = listQuery.data?.types ?? [];

  const saveMutation = useMutation({
    mutationFn: (payload: { key?: string; body: Record<string, unknown> }) => {
      if (payload.key) {
        return api(`/catalog/product-types/${payload.key}`, { method: "PUT", body: payload.body });
      }
      return api<{ type: ProductTypeDefinitionDto }>("/catalog/product-types", {
        method: "POST",
        body: payload.body,
      });
    },
    onSuccess: () => {
      toast("success", editing ? "Product type updated" : "Product type created");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["catalog-product-types"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => api(`/catalog/product-types/${key}`, { method: "DELETE" }),
    onSuccess: () => {
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["catalog-product-types"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Delete failed"),
  });

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  }

  function openEdit(row: ProductTypeDefinitionDto) {
    setEditing(row);
    setForm(rowToForm(row));
    setEditorOpen(true);
  }

  function submitType() {
    const body = {
      label: form.label.trim(),
      description: form.description.trim() || null,
      behavior: form.behavior,
      sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
      active: form.active,
      ...(editing ? {} : form.key.trim() ? { key: form.key.trim().toUpperCase() } : {}),
    };
    saveMutation.mutate({ key: editing?.key, body });
  }

  const tableRowActions = useMemo(
    () =>
      canWrite
        ? {
            onEdit: openEdit,
            onDelete: async (row: ProductTypeDefinitionDto) => {
              await deleteMutation.mutateAsync(row.key);
            },
          }
        : undefined,
    [canWrite, deleteMutation],
  );

  const tableSelection = useMemo(
    () =>
      canWrite
        ? {
            entityLabel: "Product type",
            onEdit: (rows: ProductTypeDefinitionDto[]) => openEdit(rows[0]!),
            onDelete: async (rows: ProductTypeDefinitionDto[]) => {
              await Promise.all(rows.map((r) => deleteMutation.mutateAsync(r.key)));
            },
          }
        : undefined,
    [canWrite, deleteMutation],
  );

  useBottomBarActions(() => {
    if (!canWrite) return [];
    if (editorOpen) {
      return [
        { id: "cancel", label: "Cancel", onClick: closeEditor },
        {
          id: "save",
          label: "Save",
          variant: "primary" as const,
          disabled: saveMutation.isPending || form.label.trim().length < 2,
          onClick: submitType,
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  const columns: Column<ProductTypeDefinitionDto>[] = useMemo(
    () => [
      {
        key: "label",
        header: "Type",
        sortable: true,
        sortValue: (r) => r.label,
        exportValue: (r) => `${r.label} (${r.key})`,
        render: (r) => (
          <div>
            <p className="font-medium">{r.label}</p>
            <p className="font-mono text-[10px] text-white/40">{r.key}</p>
          </div>
        ),
      },
      {
        key: "behavior",
        header: "Behavior",
        sortable: true,
        sortValue: (r) => r.behavior,
        render: (r) => <Badge color="blue">{behaviorLabel(r.behavior)}</Badge>,
      },
      {
        key: "description",
        header: "Description",
        hideSm: true,
        render: (r) => <span className="text-xs text-white/55">{r.description ?? "—"}</span>,
      },
      {
        key: "products",
        header: "Products",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.productCount ?? 0,
        render: (r) => <span className="text-xs tabular-nums">{r.productCount ?? 0}</span>,
      },
      {
        key: "sort",
        header: "Sort",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.sortOrder,
        render: (r) => <span className="text-xs tabular-nums">{r.sortOrder}</span>,
      },
      {
        key: "active",
        header: "Status",
        sortable: true,
        sortValue: (r) => r.active,
        render: (r) => (
          <Badge color={r.active ? "green" : "gray"}>{r.active ? "Active" : "Inactive"}</Badge>
        ),
      },
    ],
    [],
  );

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={types}
        rowKey={(r) => r.key}
        loading={showQueryLoader(listQuery)}
        exportFilename="product-types"
        onRowClick={canWrite ? openEdit : undefined}
        entityLabel="Product type"
        rowActions={tableRowActions}
        selection={tableSelection}
        empty={{
          icon: Tags,
          title: "No product types yet",
          hint: "Define types like merch, subscriptions, or custom bundles.",
          action: canWrite ? (
            <Button size="sm" onClick={openCreate}>
              Create
            </Button>
          ) : undefined,
        }}
      />

      <RightSidebarPanel
        open={editorOpen}
        title={editing ? "Edit product type" : "Create product type"}
        subtitle={editing?.key}
        onClose={closeEditor}
        footer={
          canWrite && (
            <div className="flex flex-wrap justify-end gap-2">
              {editing && (editing.productCount ?? 0) === 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deleteMutation.isPending}
                  onClick={() =>
                    confirm({
                      title: "Delete product type?",
                      message: `Remove ${editing.label}?`,
                      confirmLabel: "Delete",
                      danger: true,
                      action: async () => {
                        await deleteMutation.mutateAsync(editing.key);
                        toast("success", "Product type deleted");
                      },
                    })
                  }
                >
                  Delete
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={saveMutation.isPending || form.label.trim().length < 2}
                onClick={submitType}
              >
                Save
              </Button>
            </div>
          )
        }
      >
        <div className="sidebar-form">
          {!editing && (
            <Input
              label="Key"
              placeholder="MERCH (optional — generated from label)"
              value={form.key}
              onChange={(e) => setForm((f) => ({ ...f, key: e.target.value.toUpperCase() }))}
              hint="Uppercase letters, numbers, and underscores"
            />
          )}
          <Input
            label="Label"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          />
          <SearchSelect
            label="Behavior"
            options={BEHAVIOR_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
              hint: opt.hint,
            }))}
            value={form.behavior}
            onChange={(v) => setForm((f) => ({ ...f, behavior: v as ProductBehavior }))}
            disabled={Boolean(editing && (editing.productCount ?? 0) > 0)}
            hint={
              editing && (editing.productCount ?? 0) > 0
                ? "Behavior is locked while products use this type"
                : BEHAVIOR_OPTIONS.find((o) => o.value === form.behavior)?.hint
            }
          />
          <Textarea
            label="Description"
            rows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <Input
            label="Sort order"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
          />
          <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label="Active" />
        </div>
      </RightSidebarPanel>

      {dialog}
    </ListPageShell>
  );
}
